/**
 * Store hooks for editor panels (CLAUDE.md rule 3: world data never lives in
 * React state).
 *
 * Each hook selects from an immutable, identity-stable snapshot, so React
 * re-renders a component only when the value it selected actually changed:
 * renaming one entity doesn't re-render a panel watching another (review F-3).
 */
import { useRef, useSyncExternalStore } from "react";
import type { ReadableStore } from "../../core/store";
import type { EntityId, EntityRecord } from "../../world/entity";
import type { ComponentData } from "../../world/schema/defineComponent";
import type { WorldSnapshot } from "../../world/World";
import { useEditor } from "../EditorContext";
import type { Dispatch } from "../EditorContext";
import type { LoadProgress } from "../levels/LevelService";
import type { SelectionState } from "../selection/Selection";
import type { SettingsValues } from "../settings/EditorSettings";
import { useStore } from "./useStore";

/**
 * Subscribes to a store and selects part of its snapshot. The selector must
 * return a value with stable identity (a record from the snapshot, not a new
 * object), or React would re-render on every notification.
 */
export function useStoreSelector<T, R>(store: ReadableStore<T>, select: (snapshot: T) => R): R {
  return useSyncExternalStore(store.subscribe, () => select(store.getSnapshot()));
}

export function useWorldSnapshot(): WorldSnapshot {
  return useStore(useEditor().world);
}

export function useEntity(id: EntityId | null): EntityRecord | undefined {
  const { world } = useEditor();
  return useStoreSelector(world, (snapshot) =>
    id === null ? undefined : snapshot.entities.get(id),
  );
}

/**
 * The records of several entities, in the given order (missing ones left
 * out). The array keeps its identity until one of the records changes, so
 * edits elsewhere in the world don't re-render the caller.
 */
export function useEntities(ids: readonly EntityId[]): readonly EntityRecord[] {
  const { world } = useEditor();
  const last = useRef<readonly EntityRecord[]>([]);
  return useStoreSelector(world, (snapshot) => {
    const next = ids.flatMap((id) => snapshot.entities.get(id) ?? []);
    const same =
      next.length === last.current.length && next.every((record, i) => record === last.current[i]);
    if (!same) last.current = next;
    return last.current;
  });
}

export function useComponent(id: EntityId | null, type: string): ComponentData | undefined {
  const { world } = useEditor();
  return useStoreSelector(world, (snapshot) =>
    id === null ? undefined : snapshot.entities.get(id)?.components.get(type),
  );
}

export function useSelection(): SelectionState {
  return useStore(useEditor().selection);
}

export function useEditorSettings(): SettingsValues {
  return useStore(useEditor().settings);
}

export function useLoadProgress(): LoadProgress {
  return useStore(useEditor().levels.progress);
}

export function useUndoState() {
  return useStore(useEditor().bus.undoState);
}

export function useLog() {
  return useStore(useEditor().log);
}

export function useTour() {
  return useStore(useEditor().tour);
}

export function useTheme() {
  return useStore(useEditor().theme.current);
}

/** `system` or a theme id: the visitor's theme choice. */
export function useThemeChoice() {
  return useStore(useEditor().theme.choice);
}

export function useDispatch(): Dispatch {
  return useEditor().dispatch;
}
